import _, { noop, pick, constant, first, isArray, flatten, pluck } from 'underscore';
import React, { useCallback, useMemo, useRef, useState, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Panel as BootstrapPanel } from 'react-bootstrap';
import { Text, Stack, Separator } from '@fluentui/react';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import {
  Collapse,
  collapseExpander,
  getPanelErrorProps,
  TextTitle,
  useCollapseState,
  ValidatedBootstrapPanel,
  ValidatedFixedPanel,
  ValidatedPanel,
} from '@bingads-webui-campaign/wizard-jumbo-card';
import { withScope } from '@bingads-webui-react/scope-service';
import { withServerContext } from '@bingads-webui-campaign-react/server-context';
import { withInstrumentation } from '@bingads-webui-react/hoc-with-instrumentation';
import { ValidationGroup, ErrorMessages, withCustomValidation, withError } from '@bingads-webui-react/validation';
import { withStoreFromProviderId, withStoreUrl } from '@bingads-webui-campaign-react/with-store-details';
import {
  FormErrorGroup, UnmappedFormErrors as OriginalUnmappedFormErrors,
  entityIs, andComp, propertyIs, orComp, StandardControlAdapter,
} from '@bingads-webui-campaign-react/form-error';
import { AttentionBeaconGroup } from '@bingads-webui-campaign-react/attention-beacon';
import { ActuationGroup, FocusableActuator } from '@bingads-webui-campaign-react/actuator';
import { withControlErrorHandling, withUnvalidatedControlErrors, withContainerErrors } from '@bingads-webui-campaign-react/error-handling';
import { LearnMoreLink } from '@bingads-webui-campaign-react/help';
import { AssetGroup } from '@bingads-webui/campaign-odata-schemas';
import { ValidatedLegacyDateSetting, getDateSettingDetails } from '@bingads-webui-campaign/date-setting';
import { constants as ImageassetsConstants } from '@bingads-webui-campaign-react/image-experience-model';
import { ValidatedUrlPath, ValidatedMobileUrl, getAdvancedUrlOptionsCardSummaryDetails, getFinalUrlWithPath } from '@bingads-webui-campaign-react/validated-url-input';
import { getTextCardSummaryDetails, getHeadlinesCardSummaryDetails, getLongHeadlinesCardSummaryDetails, getDescriptionsCardSummaryDetails, hasTextAssets } from '@bingads-webui-campaign-react/validated-text-collection';
import {
  ValidatedStoreBackedBusinessName,
  ValidatedFinalUrlWithRecommendations,
  HeadlineAssetCollection,
  LongHeadlineAssetCollection,
  DescriptionAssetCollection,
  MAX_INLINE_RECOMMENDATIONS_PER_PAGE,
} from '@bingads-webui-campaign-react/pmax-text-input';
import { ValidatedLegacyCallToAction } from '@bingads-webui-campaign-react/legacy-call-to-action';
import { AudienceWizard } from '@bingads-webui-campaign-react/audience-signals';
import {
  ValidatedImageExperienceWrapper,
  ImageListPortalContainerWithControlErrorHandling,
  LogoListPortalContainerWithControlErrorHandling,
  ImageListSummaryCardDetails,
  PerfMaxAdPreviewUAPL,
  hasImages,
  PerfMaxImageSuggestionCarousel,
  PMaxImageExperienceMaxImageCount,
  SuggestionViewImageCount
} from '@bingads-webui-campaign-react/pmax-image-experience';
import { mergeI18n } from '@bingads-webui-campaign-react/i18n-merger';
import { withStepLoadingShell } from '@bingads-webui-campaign-react/step-wizard';
import { ValidatedListingGroups } from '@bingads-webui-campaign-react/step-wizard-controls';
import { getListingGroupDetails, getSearchThemeDetails } from '@bingads-webui-campaign-react/wizard-shared-components';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { HelpPopup } from '@bingads-webui-react/help-popup';
import {
  useRecommendations,
  withFinalUrlRecommendationUpdate,
  uniqAssetRecommendationsInThemes,
  RecommendationContext,
} from '@bingads-webui-campaign-react/perf-max-recommendation';
import { FluentProvider, webLightTheme } from '@fluentui/react-components';
import {
  AssetEventFeature,
  AssetPropertyName,
  getCopilotRecommendationIdsFromThemes,
  getFormattedThemeForApplying,
  getUniqueAssetsFromThemes,
  useCopilotRecommendationSurvey,
  useEventLog,
} from '@bingads-webui-campaign-react/copilot-common';
import {
  CopilotRecommendationBanner,
  CopilotRepositoryProvider,
  CopilotThemesRecommendationDialog,
  useBannerService,
} from '@bingads-webui/inline-copilot';
import { setClarityTag } from '@bingads-webui-universal/clarity';
import { SearchThemeSelector } from '@bingads-webui-campaign-react/search-theme-selector';
import { ValidatedUrlRules, UrlOptionsDetailsWithRules } from '@bingads-webui-campaign-react/url-rules';
import { ValidatedUrlRulesV2, UrlOptionsDetailsWithRulesV2 } from '@bingads-webui-campaign-react/url-rules-v2';
import { isCopilotRegionRestricted } from '@bingads-webui-react/copilot-utils';

import { ScenarioName } from '../../common/constants';
import {
  ServerValidatedRemainingCharsValidatedTextField,
} from '../shared';
import { useStepAssetGroupState } from './use-step-campaign-asset-group-state';
import { getAssetGroupStepStyles } from './step-campaign-asset-group-styles';
import { hasAssetsInAssetGroup } from '../../common/has-assets-in-assets-group';
import { ERROR_ENTITY } from '../../model/api-errors';
import { getExtensionFormatters } from '../../model/ad-extension';
import SparkleImg from './media/sparkle.svg';
import ImageAssetsImg from './media/image-assets.svg';
import LogoAssetsImg from './media/logo-assets.svg';
import TextAssetsImg from './media/text-assets.svg';
import CallToActionImg from './media/call-to-action.svg';
import BusinessNameImg from './media/business-name.svg';
import StartEndDateImg from './media/start-end-date.svg';
import ListingGroupImg from './media/listing-groups.svg';
import GroupInfoImg from './media/group-info.svg';
import SearchThemesImg from './media/search-themes.svg';

const entityIsAssetGroup = entityIs(ERROR_ENTITY.AssetGroup);
const assetGroupNameProp = andComp(entityIsAssetGroup, propertyIs('Name'));
const assetGroupBusinessNameProp = andComp(entityIsAssetGroup, propertyIs('BusinessName'));
const assetGroupFinalUrlsProp = andComp(entityIsAssetGroup, propertyIs('FinalUrls'));
const assetGroupPath1AndPath2Props = andComp(entityIsAssetGroup, orComp(propertyIs('Path1'), propertyIs('Path2')));
const assetGroupMobileFinalUrlsProp = andComp(entityIsAssetGroup, propertyIs('MobileFinalUrls'));
const assetGroupSearchThemesProp = andComp(entityIsAssetGroup, propertyIs('SearchThemes'));
const assetGroupImageAssetsProp = andComp(entityIsAssetGroup, propertyIs('Images'));

const ValidatedFinalUrlWithStoreURL = withError(withCustomValidation(withFinalUrlRecommendationUpdate(withStoreFromProviderId(withStoreUrl(ValidatedFinalUrlWithRecommendations)))));

const ServerValidatedFinalUrl = withControlErrorHandling(withUnvalidatedControlErrors(ValidatedFinalUrlWithStoreURL, { filter: assetGroupFinalUrlsProp }));
const ServerValidatedUrlPath = withControlErrorHandling(withUnvalidatedControlErrors(ValidatedUrlPath, { filter: assetGroupPath1AndPath2Props }));
const ServerValidatedMobileUrl = withControlErrorHandling(withUnvalidatedControlErrors(ValidatedMobileUrl, { filter: assetGroupMobileFinalUrlsProp }));
const ServerValidatedSearchThemeSelector = withControlErrorHandling(withContainerErrors(SearchThemeSelector, { filter: assetGroupMobileFinalUrlsProp, Adapter: StandardControlAdapter() }), [FocusableActuator]);
const ServerValidatedImageListPortalContainer = withUnvalidatedControlErrors(ImageListPortalContainerWithControlErrorHandling, { filter: assetGroupImageAssetsProp });
const UnmappedFormErrors = withControlErrorHandling(OriginalUnmappedFormErrors);

const AssetGroupStep = (props) => {
  const classes = useDefaultStyles(getAssetGroupStepStyles);

  const {
    stepService,
    scope,
    serverContext,
    instrumentation,
    wizardPerfMarker,
  } = props;

  const activity = useContext(ActivityContext);
  const [stepState, stepApi] = useStepAssetGroupState(stepService, activity);

  const {
    scenarioName,
    i18n,
    legacyI18n,
    locale,
    hideStepHeader,
    assetGroup,
    navigationConfirmationPrompt,
    finalUrls,
    finalUrlForRecommendation,
    headlines,
    longHeadlines,
    descriptions,
    campaignId,
    campaignName,
    campaignTimeZone,
    campaignSettings,
    suggestedObjectiveExtensions,
    otherObjectiveExtensions,
    adExtensionsDynamicFeatures = constant({}),
    adExtensionsAssociationsFactory,
    extensionsAssociations,
    urlPath1,
    urlPath2,
    finalMobileUrls,
    urlRules,
    urlRulesOption,
    images,
    businessName,
    audienceGroup,
    searchThemes,
    originalAudienceGroup,
    listingGroup,
    productFilter,
    isSaving,
    isCanceling,
    aid,
    cid,
    permissions,
    showAllValidationErrors,
    areBeaconsActive,
    serverErrors,
    systemErrors,
  } = stepState;

  const {
    onAssetGroupValidation,
    onUpdateExtensionsAssociations,
    onUpdateFinalUrls,
    onUpdateImageList,
    onClearAssets,
    onUpdateSearchThemes,
  } = stepApi;

  const {
    UAPLPMaxLogoPreviewEnabled,
    CopilotCTALogoGeneration,
    PmaxExtensionPreview,
    AssetGroupUrlRules: isAssetGroupUrlRulesEnabled,
    AssetGroupUrlRulesV2: isAssetGroupUrlRulesV2Enabled,
    PerformanceMaxInCnMarket: IsPerformanceMaxInCnMarketEnabled,
    PMaxNewAspectRatios: isPMaxNewAspectRatiosEnabled,
    PMaxExcludeNewAspectRatios: isPMaxNewAspectRatiosExcluded,
  } = permissions.dynamic;

  const isPMaxAspectRatiosV2Enabled = isPMaxNewAspectRatiosEnabled && !isPMaxNewAspectRatiosExcluded;

  const { IsDomesticSimplifiedChineseEnabled } = permissions;

  const isFeatureBlockedForCnMarket = IsDomesticSimplifiedChineseEnabled && IsPerformanceMaxInCnMarketEnabled;

  const { send: sendCopilotBannerNotification } = useBannerService();

  const [finalUrlForCopilotRecommendations, setFinalUrlForCopilotRecommendations] = useState(first(finalUrls));

  const {
    handleCopilotBannerFetch,
    handleCopilotBannerFetchSucceeded,
    handleCopilotBannerFetchFailed,
    suggestionPoller,
  } = useRecommendations({
    cid,
    aid,
    campaignId,
    permissions,
    finalUrl: finalUrlForCopilotRecommendations,
    instrumentation,
  });

  const [recommendationContext, setRecommendationContext] = useContext(RecommendationContext);
  const {
    themes, imageAssets, copilotTracking, postAssetEventLog = noop, logo, cta, isCustomerIdInExclusionList,
  } = recommendationContext;
  const finalUrlRecommendations = recommendationContext.finalUrls;

  const {
    logEnterFlow,
    logFinalUrlRecommendations,
    logBannerRefresh,
    logBannerRefreshCompleted,
    logFetch,
    logFetchSucceeded,
    logFetchAssetsSucceeded,
    logApplyThemes,
    logSuggestionsViewed,
    logPrefillAssets,
    logRemoveAssets,
  } = useEventLog(postAssetEventLog);

  const {
    triggerSurveyOnSubmit,
    triggerSurveyOnCancel,
  } = useCopilotRecommendationSurvey(AssetEventFeature.PMAX);

  useEffect(() => {
    if (isSaving) {
      triggerSurveyOnSubmit();
    }
  }, [isSaving, triggerSurveyOnSubmit]);

  useEffect(() => {
    if (isCanceling && _.size(themes) > 0) {
      triggerSurveyOnCancel();
    }
  }, [isCanceling, triggerSurveyOnCancel, themes]);

  useEffect(() => {
    activity.trace('[PmaxWizard] AssetGroupStep mounted');
  }, [activity]);

  useEffect(() => {
    setClarityTag({ PMaxCampaignsCopilotRecommendations: 'true' });
  });

  useEffect(() => {
    logEnterFlow();
  }, [logEnterFlow]);


  useEffect(() => {
    if (finalUrlRecommendations && finalUrlRecommendations.length > 0) {
      logFinalUrlRecommendations(finalUrlRecommendations);
      copilotTracking.trackReturnedFinalUrlAssets({ FinalUrls: finalUrlRecommendations });
    }
  }, [finalUrlRecommendations, logFinalUrlRecommendations, copilotTracking]);

  useEffect(() => {
    if (themes && themes.length > 0) {
      if (copilotTracking) {
        const {
          descriptionRecommendationIds, headlineRecommendationIds, longHeadlineRecommendationIds,
        } = getCopilotRecommendationIdsFromThemes(themes);

        // By default, user views first 5 copilot image and text recommendations when they are returned
        const displayedDescriptions = first(descriptionRecommendationIds, MAX_INLINE_RECOMMENDATIONS_PER_PAGE);
        const displayedHeadlines = first(headlineRecommendationIds, MAX_INLINE_RECOMMENDATIONS_PER_PAGE);
        const displayedLongHeadlines = first(longHeadlineRecommendationIds, MAX_INLINE_RECOMMENDATIONS_PER_PAGE);
        const viewedTextSuggestionIds = copilotTracking.getUntrackedViewedSuggestionIds(flatten([displayedDescriptions, displayedHeadlines, displayedLongHeadlines]));

        const displayedImages = first(pluck(imageAssets, 'suggestionId'), SuggestionViewImageCount);
        const viewedImageSuggestionIds = copilotTracking.getUntrackedViewedSuggestionIds(displayedImages);

        if (viewedTextSuggestionIds.length > 0 || viewedImageSuggestionIds.length > 0) {
          logSuggestionsViewed(viewedTextSuggestionIds, viewedImageSuggestionIds);
          copilotTracking.trackViewedSuggestionIds([...viewedTextSuggestionIds, ...viewedImageSuggestionIds]);
        }
      }
    }
  }, [copilotTracking, imageAssets, logSuggestionsViewed, themes]);

  const [
    allAssetsSections,
    assetsExpandedSections,
    setAssetsExpandedSections,
  ] = useCollapseState(
    ['final-url', 'images', 'logos', 'headlines', 'long-headlines', 'descriptions', 'business-name', 'call-to-action'] /* all sections */,
    ['final-url', 'images', 'logos', 'headlines', 'long-headlines', 'descriptions', 'business-name', 'call-to-action'] /* initially expanded sections */
  );

  const providerId = campaignSettings && campaignSettings.ProviderId;
  const finalUrlExpansion = campaignSettings && campaignSettings.length > 0 && !campaignSettings[0].FinalUrlExpansionOptOut;
  const finalUrlValidationProps = useMemo(() => ({
    data: {
      FinalUrls: finalUrls,
    },
    initialValue: {
      FinalUrls: finalUrls,
    },
    debounce: 700,
  }), [finalUrls]);
  const allAssetsExpand = useMemo(
    () => collapseExpander({ setActiveKeys: setAssetsExpandedSections }),
    [setAssetsExpandedSections]
  );

  const onRenderAdGroupNameLabel = useCallback(() => (
    <TextTitle titleKey={_TL_('Asset group name')} topicKey="pop_BA_PMAX_AssetGroupName" />
  ), []);
  const onRenderDescription = useMemo(() => (
    <Stack styles={{ root: { fontSize: 14, marginTop: 10, alignItems: 'center' } }} horizontal>
      <Text variant="small" >
        {i18n.getString(_TL_('URL expansion is on'))}
      </Text>
      <HelpPopup
        topic="pop_BA_PMAX_FinalURLExpansion"
        aria-label={i18n.getString(_TL_('Click to learn more about Final URL expansion'))}
      />
    </Stack>
  ), [i18n]);

  const [showCopilotDialog, setShowCopilotDialog] = useState(false);
  const [imageExperiencePortalContainerState, setImageExperiencePortalContainerState] = useState({});
  const [imageExperienceAddNewImageCallback, setImageExperienceAddNewImageCallback] = useState(noop);
  const [addNewDescriptionCallback, setAddNewDescriptionCallback] = useState(noop);
  const [addNewShortHeadlinesCallback, setAddNewShortHeadlinesCallback] = useState(noop);
  const [addNewLongHeadlinesCallback, setAddNewLongHeadlinesCallback] = useState(noop);
  const [newCTACallback, setNewCTACallback] = useState(noop);

  const handleRefresh = useCallback(() => {
    setShowCopilotDialog(true);
    logBannerRefresh();
  }, [logBannerRefresh]);

  const handleReset = useCallback(() => {
    onClearAssets();
    copilotTracking.removeAllInitialSuggestions();
    logRemoveAssets();
  }, [onClearAssets, logRemoveAssets, copilotTracking]);

  const handleCopilotDialogClose = useCallback(() => {
    setShowCopilotDialog(false);
  }, []);

  const maxUploadNum = useMemo(() => {
    const imageListLength = images && images.ImageList && isArray(images.ImageList) ? images.ImageList.length : 0;
    return PMaxImageExperienceMaxImageCount - imageListLength;
  }, [images]);

  // only prefill for new asset groups & when customer is not in exclusion list
  const [shouldPrefill, setShouldPrefill] = useState(assetGroup.data.Id == null);

  useEffect(() => {
    if (shouldPrefill && _.size(themes) > 0) {
      if (!hasAssetsInAssetGroup(assetGroup.data)) {
        setShouldPrefill(false);

        if (isCustomerIdInExclusionList) {
          activity.trace('not prefilling asset group, customer is in exclusion list');
          return;
        }

        // new asset group with no text or image assets, prefill with copilot recommendations
        activity.trace('prefilling asset group with copilot recommendations');
        sendCopilotBannerNotification('Apply');

        if (addNewShortHeadlinesCallback) {
          const newHeadlines = getUniqueAssetsFromThemes(themes, 'headlines', 12);
          copilotTracking.trackInitialSuggestions(AssetPropertyName.HEADLINE, newHeadlines);

          addNewShortHeadlinesCallback(newHeadlines);
        }

        if (addNewLongHeadlinesCallback) {
          const newLongHeadlines = getUniqueAssetsFromThemes(themes, 'longHeadlines', 4);
          copilotTracking.trackInitialSuggestions(AssetPropertyName.LONG_HEADLINE, newLongHeadlines);

          addNewLongHeadlinesCallback(newLongHeadlines);
        }

        if (addNewDescriptionCallback) {
          const newDescriptions = getUniqueAssetsFromThemes(themes, 'descriptions', 4);
          copilotTracking.trackInitialSuggestions(AssetPropertyName.DESCRIPTION, newDescriptions);

          addNewDescriptionCallback(newDescriptions);
        }

        // prefill images
        new Promise((resolve) => {
          if (imageExperienceAddNewImageCallback) {
            const newImages = getUniqueAssetsFromThemes(themes, 'images', 5);
            if (newImages.length > 0) {
              resolve(imageExperienceAddNewImageCallback(newImages, ImageassetsConstants.imageOptionKeys.Image));
              return;
            }
          }
          resolve({ processedImages: [] });
        }).then((response) => {
          if (!_.isEmpty(response.processedImages)) {
            const newImagesAssets = _.map(response.processedImages, image => image.idString);
            copilotTracking.trackInitialSuggestions(AssetPropertyName.IMAGE, newImagesAssets);
          }

          if (response.errorMessage && activity) {
            const message = `Apply image from copilot prefill error: ${response.errorMessage}`;
            activity.trace(message);
          }
        }).then(() => {
          if (cta && newCTACallback && assetGroup.data && assetGroup.data.CallToAction !== 'Automated') {
            newCTACallback({
              CallToAction: cta.replace(' ', ''),
            });
          }
          if (logo) {
            return imageExperienceAddNewImageCallback([{
              fullUrl: logo,
              suggestionId: -1,
            }], ImageassetsConstants.imageOptionKeys.Logo);
          }
          return Promise.resolve();
        }).finally(() => {
          sendCopilotBannerNotification('ApplySucceeded');
          logPrefillAssets();
        });
      } else {
        activity.trace('not prefilling asset group, text or image assets already exist');
      }
    }
  }, [
    activity,
    addNewDescriptionCallback,
    addNewLongHeadlinesCallback,
    addNewShortHeadlinesCallback,
    assetGroup.data,
    imageExperienceAddNewImageCallback,
    logPrefillAssets,
    sendCopilotBannerNotification,
    shouldPrefill,
    themes,
    cta,
    logo,
    newCTACallback,
    copilotTracking,
    isCustomerIdInExclusionList,
  ]);

  const onApplyCopilotRecommendation = useCallback((selectedThemes, selectedThemesAndAssets) => {
    logApplyThemes(selectedThemes);

    const formattedThemesAndAssets = _.map(selectedThemesAndAssets, getFormattedThemeForApplying);

    sendCopilotBannerNotification('Apply');
    setRecommendationContext({
      themes: [...formattedThemesAndAssets],
      ...uniqAssetRecommendationsInThemes(formattedThemesAndAssets),
    });

    if (addNewShortHeadlinesCallback) {
      addNewShortHeadlinesCallback(getUniqueAssetsFromThemes(formattedThemesAndAssets, 'headlines'));
    }
    if (addNewLongHeadlinesCallback) {
      addNewLongHeadlinesCallback(getUniqueAssetsFromThemes(formattedThemesAndAssets, 'longHeadlines'));
    }
    if (addNewDescriptionCallback) {
      addNewDescriptionCallback(getUniqueAssetsFromThemes(formattedThemesAndAssets, 'descriptions'));
    }

    const logCopilotViewedImageSuggestions = (suggestionIds) => {
      if (copilotTracking) {
        const viewedImageSuggestionIds = copilotTracking.getUntrackedViewedSuggestionIds(suggestionIds);
        if (viewedImageSuggestionIds.length > 0) {
          logSuggestionsViewed([], viewedImageSuggestionIds);
          copilotTracking.trackViewedSuggestionIds(viewedImageSuggestionIds);
        }
      }
    };

    const availableNewImages = getUniqueAssetsFromThemes(formattedThemesAndAssets, 'images', maxUploadNum);
    if (imageExperienceAddNewImageCallback && availableNewImages && availableNewImages.length > 0) {
      imageExperienceAddNewImageCallback(availableNewImages, ImageassetsConstants.imageOptionKeys.Image)
        .then((response) => {
          if (response.processedImages) {
            logCopilotViewedImageSuggestions(_.pluck(response.processedImages, 'suggestionId'));
            response.processedImages.forEach((image) => {
              copilotTracking.trackSelectedImageAssets({ ImageAssets: [{ suggestionId: image.suggestionId, imageId: image.idString }] });
            });
          }
          if (response.errorMessage && activity) {
            const message = `Apply image from copilot dialog error: ${response.errorMessage}`;
            activity.trace(message);
          }
        })
        .finally(() => {
          sendCopilotBannerNotification('ApplySucceeded');
        });
    } else {
      sendCopilotBannerNotification('ApplySucceeded');
    }
    handleCopilotDialogClose();
  }, [sendCopilotBannerNotification, setRecommendationContext, addNewShortHeadlinesCallback, addNewLongHeadlinesCallback, addNewDescriptionCallback, imageExperienceAddNewImageCallback, handleCopilotDialogClose, maxUploadNum, activity, copilotTracking, logSuggestionsViewed, logApplyThemes]);

  const onFinalURLFocusOut = useCallback(() => {
    // to prevent sending too many requests, only update the copilot recommendations after user has finished editing URL
    setFinalUrlForCopilotRecommendations(finalUrlForRecommendation);
  }, [finalUrlForRecommendation]);

  const onFinalUrlEnter = useCallback((e) => {
    const { key } = e;
    if (key === 'Enter') {
      setFinalUrlForCopilotRecommendations(finalUrlForRecommendation);
    }
  }, [finalUrlForRecommendation]);

  const finalUrlAdditionalProps = useMemo(() => ({
    onBlur: onFinalURLFocusOut,
    onKeyUp: onFinalUrlEnter,
  }), [onFinalUrlEnter, onFinalURLFocusOut]);

  const handleFetchThemes = useCallback(() => {
    logFetch();
  }, [logFetch]);

  const handleFetchThemesSucceeded = useCallback((finalUrl, themesList) => {
    logFetchSucceeded(finalUrl, themesList);
  }, [logFetchSucceeded]);

  const handleFetchAssetsSucceeded = useCallback((finalUrl, assetsRecommendationData) => {
    logFetchAssetsSucceeded(finalUrl, assetsRecommendationData);
    if (copilotTracking) {
      copilotTracking.trackReturnedTextAssets(assetsRecommendationData.RecommendedAssets);
    }
  }, [logFetchAssetsSucceeded, copilotTracking]);

  const [isRegionRestricted, setIsRegionRestricted] = useState(true);
  useEffect(() => {
    isCopilotRegionRestricted({
      feature: 'Recommendations',
      permissions,
      serverContext,
    }).then(setIsRegionRestricted);
  }, [permissions, serverContext]);

  const validateSpecialCharacters = permissions && permissions.dynamic && permissions.dynamic.PerformanceMaxCampaignsFinalURLValidation && permissions.dynamic.PMaxAndDSAFinalURLValidation;

  const assetGroupNameSection = useMemo(() => (
    <div className={classNames('asset-group-name-section')}>
      <Collapse allPanelItems={['asset-group-info']} checkItems skipItemsTypeCheck activeKeys={['asset-group-info']} setActiveKeys={setAssetsExpandedSections}>
        <ValidatedFixedPanel
          key="asset-group-info"
          title={<TextTitle
            titleKey={_TL_('Asset group information')}
            icon={GroupInfoImg}
          />}
          attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
        >
          <ServerValidatedRemainingCharsValidatedTextField
            placeholder={i18n.getString(_TL_('Enter your asset group name'))}
            id="input-asset-group-name"
            validationProps={{
              field: 'Name',
              schema: AssetGroup,
            }}
            i18n={i18n}
            label={i18n.getString(_TL_('Asset group name'))}
            onRenderLabel={onRenderAdGroupNameLabel}
            formErrorProps={{ filter: assetGroupNameProp }}
          />
          <ServerValidatedFinalUrl
            i18n={i18n}
            validationProps={finalUrlValidationProps}
            permissions={permissions}
            providerId={providerId}
            validateOnRender
            additionalProperties={finalUrlAdditionalProps}
            enableSpecialCharactersValidation={validateSpecialCharacters}
          />
          {finalUrlExpansion && onRenderDescription}
        </ValidatedFixedPanel>
      </Collapse>
      {!isRegionRestricted && (
        <FluentProvider theme={webLightTheme}>
          <CopilotRecommendationBanner
            isToastEnabled
            onRefresh={handleRefresh}
            onReset={handleReset}
            toastOffset={scenarioName === ScenarioName.PerformanceMaxCampaignCreation ? { horizontal: 260, vertical: 120 } : undefined}
            onBannerRefreshCompleted={logBannerRefreshCompleted}
            shouldFetchRecommendations
            onFetch={handleCopilotBannerFetch}
            onFetchFailed={handleCopilotBannerFetchFailed}
            onFetchSucceeded={handleCopilotBannerFetchSucceeded}
          />
          {showCopilotDialog &&
            <CopilotThemesRecommendationDialog
              open
              onApply={onApplyCopilotRecommendation}
              onClosed={handleCopilotDialogClose}
              onFetch={handleFetchThemes}
              onFetchSucceeded={handleFetchThemesSucceeded}
              onFetchAssetsSucceeded={handleFetchAssetsSucceeded}
            />
          }
        </FluentProvider>
      )}
    </div>
  ), [
    finalUrlAdditionalProps,
    finalUrlExpansion,
    finalUrlValidationProps,
    handleCopilotDialogClose,
    handleRefresh,
    i18n,
    isRegionRestricted,
    onApplyCopilotRecommendation,
    handleCopilotBannerFetch,
    handleCopilotBannerFetchFailed,
    handleCopilotBannerFetchSucceeded,
    logBannerRefreshCompleted,
    handleReset,
    onRenderAdGroupNameLabel,
    onRenderDescription,
    permissions,
    handleFetchThemes,
    handleFetchThemesSucceeded,
    handleFetchAssetsSucceeded,
    providerId,
    scenarioName,
    setAssetsExpandedSections,
    showCopilotDialog,
  ]);

  const [
    allListingGroupsSections,
    listingGroupsExpandedSections,
    setListingGroupsExpandedSections,
  ] = useCollapseState(
    ['listing-groups'] /* all sections */,
    ['listing-groups'] /* initially expanded sections */
  );

  const listingGroupExpand = useMemo(
    () => collapseExpander({ setActiveKeys: setListingGroupsExpandedSections }),
    [setListingGroupsExpandedSections]
  );

  const mergedI18n = useMemo(() => mergeI18n(
    legacyI18n,
    i18n
  ), [i18n, legacyI18n]);

  const listingGroupDetails = useMemo(() => getListingGroupDetails(
    i18n,
    !!scope.values.assetGroupId,
    {
      productFilter,
      listingGroup,
    }
  ), [i18n, listingGroup, productFilter, scope.values.assetGroupId]);

  const listingGroupsSection = useMemo(() => (
    <div className={classNames('listing-groups-section')}>
      <h3 className="section-title">{i18n.getString(_TL_('Add products'))}</h3>
      <div className="section-description">
        <span >{i18n.getString(_TL_('Choose which products you want to display in your ads. Some of your ads will use images, headlines, and descriptions from Merchant Center.'))}</span>
      </div>
      <Collapse allPanelItems={allListingGroupsSections} checkItems skipItemsTypeCheck activeKeys={listingGroupsExpandedSections} setActiveKeys={setListingGroupsExpandedSections}>
        <ValidatedPanel
          key="listing-groups"
          title={<TextTitle
            titleKey={_TL_('Listing groups')}
            details={listingGroupDetails}
            icon={ListingGroupImg}
          />}
          attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
          actuationProps={{
            expand: listingGroupExpand,
            expandKey: 'listing-groups',
          }}
        >
          <ValidatedListingGroups
            i18n={mergedI18n}
            validationProps={{}}
            campaignId={campaignId}
            campaignSettings={campaignSettings}
            listingGroup={listingGroup}
            providerId={providerId}
          />
        </ValidatedPanel>
      </Collapse>
    </div>
  ), [i18n, allListingGroupsSections, listingGroupsExpandedSections, setListingGroupsExpandedSections, listingGroupDetails, listingGroupExpand, mergedI18n, campaignId, campaignSettings, listingGroup, providerId]);


  const imagesContainerRef = useRef(null);
  const logosContainerRef = useRef(null);
  const collectionsSummary = [
    { collection: headlines, displayText: i18n.getString(_TL_('short headlines')) },
    { collection: longHeadlines, displayText: i18n.getString(_TL_('long headlines')) },
    { collection: descriptions, displayText: i18n.getString(_TL_('descriptions')) },
  ];

  const textCardSummaryDetails = useMemo(() => getTextCardSummaryDetails(collectionsSummary, i18n), [collectionsSummary, i18n]);
  const headlinesCardSummaryDetails = useMemo(() => getHeadlinesCardSummaryDetails(i18n, headlines), [headlines, i18n]);
  const longHeadlinesCardSummaryDetails = useMemo(() => getLongHeadlinesCardSummaryDetails(i18n, longHeadlines), [longHeadlines, i18n]);
  const descriptionsCardSummaryDetails = useMemo(() => getDescriptionsCardSummaryDetails(i18n, descriptions), [descriptions, i18n]);

  const textAndImageAssetsOptional = useMemo(() => providerId && !hasImages(images) && !hasTextAssets(headlines, longHeadlines, descriptions), [providerId, images, headlines, longHeadlines, descriptions]);

  const assetsSection = useMemo(() => (
    <div className={classNames('asset-group-assets-section')}>
      <h3 className="section-title">{i18n.getString(_TL_('Add assets'))}</h3>
      <div className="section-description">
        <span>{i18n.getString(_TL_('Provide your assets and we’ll automatically determine what your ads look like, where and when they appear, and who sees them.'))}</span>
      </div>
      <Collapse allPanelItems={allAssetsSections} checkItems skipItemsTypeCheck activeKeys={assetsExpandedSections} setActiveKeys={setAssetsExpandedSections}>
        <ValidatedPanel
          key="images"
          title={<TextTitle
            titleKey={_TL_('Images')}
            topicKey={isPMaxAspectRatiosV2Enabled ? 'pop_BA_PMAX_UpdatedAspectRatio' : 'pop_BA_ImageAssets_PMAX'}
            icon={ImageAssetsImg}
            iconAltText={i18n.getString(_TL_('"Images" panel\'s image'))}
            details={<ImageListSummaryCardDetails i18n={i18n} images={images} isLogos={false} />}
          />}
          attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
          actuationProps={{
            expand: allAssetsExpand,
            expandKey: 'images',
          }}
        >
          <span>{i18n.getString(_TL_('You can add up to 20 images to your ad. We recommend that you add two to three relevant images. Required aspect ratios: 1.91:1 and 1:1. Minimum image upload dimensions: 703 x 368 pixels. '))}</span>
          <ServerValidatedImageListPortalContainer
            i18n={i18n}
            containerRef={imagesContainerRef}
            containerState={imageExperiencePortalContainerState}
            isLogos={false}
          />
          <PerfMaxImageSuggestionCarousel i18n={i18n} addImageCallback={imageExperienceAddNewImageCallback} permissions={permissions} maxUploadNum={maxUploadNum} />
        </ValidatedPanel>
        <ValidatedPanel
          key="logos"
          title={<TextTitle
            titleKey={_TL_('Logos')}
            icon={LogoAssetsImg}
            iconAltText={i18n.getString(_TL_('"Logos" panel\'s image'))}
            topicKey="pop_BA_CampaignCreate_MMA_Logos"
            details={<ImageListSummaryCardDetails i18n={i18n} images={images} isLogos />}
          />}
          attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
          actuationProps={{
            expand: allAssetsExpand,
            expandKey: 'logos',
          }}
        >
          <span>{i18n.getString(_TL_('You can add up to 5 different logos. Each logo can have one or both 4:1 aspect ratio and 1:1 aspect ratio.'))}</span>
          <LogoListPortalContainerWithControlErrorHandling
            i18n={i18n}
            containerRef={logosContainerRef}
            containerState={imageExperiencePortalContainerState}
            isLogos
          />
        </ValidatedPanel>

        <ValidatedPanel
          key="headlines"
          title={<TextTitle
            titleKey={_TL_('Short headlines')}
            icon={TextAssetsImg}
            iconAltText={i18n.getString(_TL_('Short headlines'))}
            details={headlinesCardSummaryDetails}
          />}
          attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
          actuationProps={{
            expand: allAssetsExpand,
            expandKey: 'headlines',
          }}
        >
          <HeadlineAssetCollection isOptional={textAndImageAssetsOptional} showDeleteButton setAddNewShortHeadlinesCallback={setAddNewShortHeadlinesCallback} />
        </ValidatedPanel>

        <ValidatedPanel
          key="long-headlines"
          title={<TextTitle
            titleKey={_TL_('Long headlines')}
            icon={TextAssetsImg}
            iconAltText={i18n.getString(_TL_('Long headlines'))}
            details={longHeadlinesCardSummaryDetails}
          />}
          attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
          actuationProps={{
            expand: allAssetsExpand,
            expandKey: 'long-headlines',
          }}
        >
          <LongHeadlineAssetCollection isOptional={textAndImageAssetsOptional} showDeleteButton setAddNewLongHeadlinesCallback={setAddNewLongHeadlinesCallback} />
        </ValidatedPanel>

        <ValidatedPanel
          key="descriptions"
          title={<TextTitle
            titleKey={_TL_('Descriptions')}
            icon={TextAssetsImg}
            iconAltText={i18n.getString(_TL_('Descriptions'))}
            details={descriptionsCardSummaryDetails}
          />}
          attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
          actuationProps={{
            expand: allAssetsExpand,
            expandKey: 'descriptions',
          }}
        >
          <DescriptionAssetCollection isOptional={textAndImageAssetsOptional} showDeleteButton setAddNewDescriptionCallback={setAddNewDescriptionCallback} />
        </ValidatedPanel>

        <ValidatedFixedPanel
          key="business-name"
          title={
            <TextTitle
              titleKey={_TL_('Business name')}
              topicKey="pop_BA_CampaignCreate_AudAds_BusinessName"
              icon={BusinessNameImg}
              iconAltText={i18n.getString(_TL_('"Business name" panel\'s image'))}
            />
          }
          attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
        >
          <ValidatedStoreBackedBusinessName
            id="input-business-name"
            providerId={providerId}
            i18n={i18n}
            validationProps={{
              data: { BusinessName: businessName },
              initialValue: { BusinessName: businessName },
            }}
            formErrorProps={{ filter: assetGroupBusinessNameProp }}
          />
        </ValidatedFixedPanel>
        <ValidatedFixedPanel
          key="call-to-action"
          title={
            <TextTitle
              titleKey={_TL_('Call-to-action')}
              topicKey="pop_BA_PMAX_CallToAction"
              icon={CallToActionImg}
              iconAltText={i18n.getString(_TL_('"Call to action" panel\'s image'))}
            />
          }
          noOverflowHidden
          relativePosition
          attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
        >
          <ValidatedLegacyCallToAction
            locale={locale}
            permissions={permissions}
            validationProps={pick(assetGroup.data, 'CallToAction')}
            isPmaxAd={permissions.IsPerformanceMaxAutomatedCTAEnabled && !isFeatureBlockedForCnMarket}
            setNewCTACallback={setNewCTACallback}
          />
        </ValidatedFixedPanel>
      </Collapse>
    </div>
  ), [i18n, allAssetsSections, assetsExpandedSections, setAssetsExpandedSections, isPMaxAspectRatiosV2Enabled, images, allAssetsExpand, imageExperiencePortalContainerState, imageExperienceAddNewImageCallback, permissions, maxUploadNum, headlinesCardSummaryDetails, textAndImageAssetsOptional, longHeadlinesCardSummaryDetails, descriptionsCardSummaryDetails, textCardSummaryDetails, providerId, businessName, locale, assetGroup.data, isFeatureBlockedForCnMarket]);

  const getAdExtensionsSections = useCallback(
    extensionTypes => (scenarioName === ScenarioName.PerformanceMaxCampaignCreation ?
      adExtensionsAssociationsFactory.extensionsAssociationsFactory(campaignId, campaignName, extensionTypes, adExtensionsDynamicFeatures) : [])
      .reduce((memo, {
        extensionType,
        title,
        topic,
        details,
        AssociationPanel,
      }) => {
        const sectionKey = extensionType;
        const associations = extensionsAssociations[extensionType];

        const panel = (
          <Collapse.Panel
            key={sectionKey}
            title={<TextTitle titleKey={title} topicKey={topic} details={details(i18n, associations)} />}
          >
            <AssociationPanel
              i18n={i18n}
              extensionsAssociations={extensionsAssociations}
              onUpdateExtensionsAssociations={onUpdateExtensionsAssociations}
            />
          </Collapse.Panel>

        );
        memo.panels.push(panel);
        memo.sectionsInfo.allSections.push(sectionKey);

        return memo;
      }, { panels: [], sectionsInfo: { allSections: [] } }),
    [scenarioName, i18n, campaignId, campaignName, adExtensionsDynamicFeatures, adExtensionsAssociationsFactory, extensionsAssociations, onUpdateExtensionsAssociations]
  );

  const suggestedAdExtensionsSections = useMemo(() => getAdExtensionsSections(suggestedObjectiveExtensions), [getAdExtensionsSections, suggestedObjectiveExtensions]);
  const otherAdExtensionsSections = useMemo(() => getAdExtensionsSections(otherObjectiveExtensions), [getAdExtensionsSections, otherObjectiveExtensions]);

  const [
    allEnhanceYourAdsSections,
    enhanceYourAdsExpandedSections,
    setEnhanceYourAdsExpandedSections,
  ] = useCollapseState(
    suggestedAdExtensionsSections.sectionsInfo.allSections /* all sections */,
    [] /* initially expanded sections */
  );

  const enhanceYourAdsSection = useMemo(() => (
    <>
      {suggestedAdExtensionsSections.panels.length > 0 &&
        <div className={classNames('asset-group-enhance-ads-section')}>
          <h3 className="section-title">{i18n.getString(_TL_('Add more information to your ads'))}</h3>
          <div className="section-description">
            <span>
              <LearnMoreLink
                topic="app54489"
                text={i18n.getString(_TL_('Make your ads larger and more eye-catching by including additional information in your ads.'))}
                linkText={i18n.getString(_TL_('Learn more'))}
              />
            </span>
          </div>
          <Collapse allPanelItems={allEnhanceYourAdsSections} checkItems skipItemsTypeCheck activeKeys={enhanceYourAdsExpandedSections} setActiveKeys={setEnhanceYourAdsExpandedSections}>
            {suggestedAdExtensionsSections.panels}
          </Collapse>
        </div>
      }
    </>
  ), [i18n, allEnhanceYourAdsSections, enhanceYourAdsExpandedSections, setEnhanceYourAdsExpandedSections, suggestedAdExtensionsSections]);

  const [
    allMoreAssetsSections,
    moreAssetsExpandedSections,
    setMoreAssetsExpandedSections,
  ] = useCollapseState(
    otherAdExtensionsSections.sectionsInfo.allSections /* all sections */,
    [] /* initially expanded sections */
  );

  const moreAssetsSection = useMemo(() => (
    <>
      {otherAdExtensionsSections.panels.length > 0 &&
        <ValidatedBootstrapPanel
          defaultExpanded={false}
          className={classNames(classes.collapsiblePanel, 'asset-group-more-assets-panel')}
          render={() => (
            <>
              <BootstrapPanel.Heading>
                <BootstrapPanel.Title toggle>
                  {i18n.getString(_TL_('More assets'))}
                </BootstrapPanel.Title>
              </BootstrapPanel.Heading>
              <BootstrapPanel.Collapse>
                <BootstrapPanel.Body>
                  <div className={classNames('asset-group-more-assets-section')}>
                    <Collapse allPanelItems={allMoreAssetsSections} checkItems skipItemsTypeCheck activeKeys={moreAssetsExpandedSections} setActiveKeys={setMoreAssetsExpandedSections}>
                      {otherAdExtensionsSections.panels}
                    </Collapse>
                  </div>
                </BootstrapPanel.Body>
              </BootstrapPanel.Collapse>
            </>
          )}
        />
      }
    </>
  ), [i18n, classes.collapsiblePanel, allMoreAssetsSections, moreAssetsExpandedSections, setMoreAssetsExpandedSections, otherAdExtensionsSections.panels]);

  const [
    allMoreOptionsSections,
    moreOptionsExpandedSections,
    setMoreOptionsExpandedSections,
  ] = useCollapseState(
    ['advanced-url-options'] /* all sections */,
    ['advanced-url-options'] /* initially expanded sections */
  );
  const moreOptionsExpand = useMemo(
    () => collapseExpander({ setActiveKeys: setMoreOptionsExpandedSections }),
    [setMoreOptionsExpandedSections]
  );

  const [moreOptionsSectionExpanded, setNameAndTitleExpanded] = useState(false);
  const onMoreOptionsSectionToggle = useCallback((expanded) => {
    setNameAndTitleExpanded(expanded);
  }, []);

  const moreOptionsSection = useMemo(() => (
    <ValidatedBootstrapPanel
      onToggle={onMoreOptionsSectionToggle}
      expanded={moreOptionsSectionExpanded}
      // defaultExpanded={false}
      className={classNames(classes.collapsiblePanel, 'more-asset-group-options')}
      render={() => (
        <>
          <BootstrapPanel.Heading>
            <BootstrapPanel.Title toggle>
              {i18n.getString(_TL_('More options'))}
            </BootstrapPanel.Title>
          </BootstrapPanel.Heading>
          <BootstrapPanel.Collapse>
            <BootstrapPanel.Body>
              <div className={classNames('asset-group-more-options-section')}>
                <AttentionBeaconGroup>
                  <Collapse allPanelItems={allMoreOptionsSections} checkItems skipItemsTypeCheck activeKeys={moreOptionsExpandedSections} setActiveKeys={setMoreOptionsExpandedSections}>
                    <ValidatedPanel
                      key="advanced-url-options"
                      title={<TextTitle
                        titleKey={_TL_('Advanced URL options')}
                        // eslint-disable-next-line no-nested-ternary
                        details={!isAssetGroupUrlRulesEnabled
                          ? getAdvancedUrlOptionsCardSummaryDetails(i18n, finalUrls, urlPath1, urlPath2, finalMobileUrls)
                          : isAssetGroupUrlRulesV2Enabled
                            ? <UrlOptionsDetailsWithRulesV2 i18n={i18n} finalUrls={finalUrls} path1={urlPath1} path2={urlPath2} mobileFinalUrls={finalMobileUrls} urlRules={urlRules} />
                            : <UrlOptionsDetailsWithRules i18n={i18n} finalUrls={finalUrls} path1={urlPath1} path2={urlPath2} mobileFinalUrls={finalMobileUrls} urlRules={urlRules} />}
                      />}
                      attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
                      actuationProps={{
                        expand: moreOptionsExpand,
                        expandKey: 'advanced-url-options',
                      }}
                    >
                      <ServerValidatedUrlPath
                        i18n={i18n}
                        validationProps={{ data: { Path1: urlPath1, Path2: urlPath2, FinalUrls: finalUrls } }}
                      />
                      <ServerValidatedMobileUrl
                        i18n={i18n}
                        validationProps={{ data: { MobileFinalUrls: finalMobileUrls } }}
                      />
                      {
                        isAssetGroupUrlRulesEnabled && !isAssetGroupUrlRulesV2Enabled && (
                          <>
                            <Separator />
                            <ValidatedUrlRules
                              i18n={i18n}
                              validationProps={{ data: { urlRules, urlRulesOption } }}
                            />
                          </>
                        )
                      }
                      {
                        isAssetGroupUrlRulesV2Enabled && (
                          <>
                            <Separator />
                            <ValidatedUrlRulesV2
                              i18n={i18n}
                              validationProps={{ data: { urlRules } }}
                            />
                          </>
                        )
                      }
                    </ValidatedPanel>
                  </Collapse>
                </AttentionBeaconGroup>
              </div>
            </BootstrapPanel.Body>
          </BootstrapPanel.Collapse>
        </>
      )}
    />
  ), [onMoreOptionsSectionToggle, moreOptionsSectionExpanded, classes.collapsiblePanel, i18n, allMoreOptionsSections, moreOptionsExpandedSections, setMoreOptionsExpandedSections, isAssetGroupUrlRulesEnabled, isAssetGroupUrlRulesV2Enabled, finalUrls, urlPath1, urlPath2, finalMobileUrls, urlRules, moreOptionsExpand, urlRulesOption]);

  const isReadOnlyUser = useMemo(() => permissions.IsReadOnlyUser || serverContext.CurrentAccount.Status === 32, [permissions.IsReadOnlyUser, serverContext.CurrentAccount.Status]); // 32 is InActive

  const audienceSignalSection = useMemo(() => (
    <div className={classNames('asset-group-audience-signal-section')}>
      <h3 className="section-title">{i18n.getString(_TL_('Audience signals'))} <span className={classes.h3LabelOptional}>{i18n.getString(_TL_('(OPTIONAL)'))}</span></h3>
      <div className={classNames(classes.audienceSignalDescription, 'section-description')}>
        <img src={SparkleImg} alt="" />
        <span className="audience-signal-main-description">{i18n.getString(_TL_('Audience signals help us reach the right audience for your ads and maximize your conversions.'))}</span>
        <span className="audience-signal-sub-description">{i18n.getString(_TL_('Create an audience signal or choose from your existing audience signals. We’ll use them as a starting point to find customers and determine who sees your ads.'))}</span>
      </div>
      <Collapse allPanelItems={['audience-signal']} checkItems skipItemsTypeCheck activeKeys={['audience-signal']} setActiveKeys={noop}>
        <ValidatedFixedPanel key="audience-signal" attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}>
          <div className={classes.audienceWizard}>
            <AudienceWizard
              audienceGroup={audienceGroup}
              originalAudienceGroup={originalAudienceGroup}
              aid={aid}
              cid={cid}
              i18n={i18n}
              isReadOnlyUser={isReadOnlyUser}
              wizardPerfMarker={wizardPerfMarker}
            />
          </div>
        </ValidatedFixedPanel>
      </Collapse>
    </div>
  ), [i18n, classes.h3LabelOptional, classes.audienceSignalDescription, classes.audienceWizard, audienceGroup, originalAudienceGroup, aid, cid, isReadOnlyUser, wizardPerfMarker]);

  const [
    allMoreSettingsSections,
    moreSettingsExpandedSections,
    setMoreSettingsExpandedSections,
  ] = useCollapseState(
    permissions.dynamic.SearchThemes ? ['start-end-dates', 'search-themes'] : ['start-end-dates'] /* all sections */,
    permissions.dynamic.SearchThemes ? ['start-end-dates', 'search-themes'] : ['start-end-dates'] /* initially expanded sections */
  );
  const moreSettingsExpand = useMemo(
    () => collapseExpander({ setActiveKeys: setMoreSettingsExpandedSections }),
    [setMoreSettingsExpandedSections]
  );

  const searchThemesSection = useMemo(() => (
    <div className={classNames('search-theme-settings-section')}>
      <Collapse allPanelItems={allMoreSettingsSections} checkItems skipItemsTypeCheck activeKeys={moreSettingsExpandedSections} setActiveKeys={setMoreSettingsExpandedSections}>
        <ValidatedPanel
          key="search-themes"
          title={<TextTitle
            titleKey={_TL_('Search Themes')}
            topicKey="pop_BA_PMAX_SearchThemes"
            details={getSearchThemeDetails(i18n, searchThemes)}
            icon={SearchThemesImg}
          />}
          attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
          actuationProps={{
            expand: moreSettingsExpand,
            expandKey: 'search-themes',
          }}
        >
          <ServerValidatedSearchThemeSelector
            i18n={i18n}
            wizardPerfMarker={wizardPerfMarker}
            searchThemes={searchThemes}
            onUpdateSearchThemes={onUpdateSearchThemes}
            formErrorProps={{ filter: assetGroupSearchThemesProp }}
          />
        </ValidatedPanel>
      </Collapse>
    </div>
  ), [allMoreSettingsSections, moreSettingsExpandedSections, setMoreSettingsExpandedSections, i18n, searchThemes, moreSettingsExpand, wizardPerfMarker, onUpdateSearchThemes]);

  const moreSettingsSection = useMemo(() => (
    <div className={classNames('more-settings-section')}>
      <h6 className="page-title">{i18n.getString(_TL_('MORE SETTINGS'))}</h6>
      <Collapse allPanelItems={allMoreSettingsSections} checkItems skipItemsTypeCheck activeKeys={moreSettingsExpandedSections} setActiveKeys={setMoreSettingsExpandedSections}>
        <ValidatedPanel
          key="start-end-dates"
          title={<TextTitle
            titleKey={_TL_('Start and end dates')}
            details={getDateSettingDetails(i18n, assetGroup.data.StartDate)}
            icon={StartEndDateImg}
          />}
          attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
          actuationProps={{
            expand: moreSettingsExpand,
            expandKey: 'start-end-dates',
          }}
        >
          <ValidatedLegacyDateSetting
            i18n={i18n}
            validationProps={{
              data: {
                StartDate: assetGroup.data.StartDate,
                EndDate: assetGroup.data.EndDate,
                TimeZone: campaignTimeZone,
                isCreate: !assetGroup.data.Id,
              },
            }}
          />
        </ValidatedPanel>
      </Collapse>
    </div>
  ), [i18n, allMoreSettingsSections, moreSettingsExpandedSections, setMoreSettingsExpandedSections, assetGroup.data.StartDate, assetGroup.data.EndDate, assetGroup.data.Id, campaignTimeZone, moreSettingsExpand]);

  const [previewImagesSharedState, setPreviewImagesSharedState] = useState({
    previewImageId: null,
    previewLogoId: null,
    cropImageObj: null,
    editingPlacementType: '',
  });

  const imageExperienceComponents = useMemo(() => (
    <div>
      <ValidatedImageExperienceWrapper
        i18n={i18n}
        permissions={permissions}
        imagesContaineRef={imagesContainerRef}
        logosContaineRef={logosContainerRef}
        campaignId={campaignId}
        imageAssetsOptional={textAndImageAssetsOptional}
        updateFinalUrlCallback={onUpdateFinalUrls}
        previewImagesSharedState={previewImagesSharedState}
        setPreviewImagesSharedState={setPreviewImagesSharedState}
        updatePortalContainerState={setImageExperiencePortalContainerState}
        setImageExperienceAddNewImageCallback={setImageExperienceAddNewImageCallback}
        validationProps={{ data: assetGroup.data, initialValue: assetGroup.data }}
        suggestionPoller={suggestionPoller}
        wizardPerfMarker={wizardPerfMarker}
        postAssetEventLog={postAssetEventLog}
        UAPLPMaxIntegration
        showCopilotOptionForImage={permissions.IsBackgroundSceneEnabled}
        isPMaxAspectRatiosV2Enabled={isPMaxAspectRatiosV2Enabled}
      />
    </div>), [i18n, permissions, campaignId, textAndImageAssetsOptional, onUpdateFinalUrls, previewImagesSharedState, assetGroup.data, suggestionPoller, wizardPerfMarker, postAssetEventLog, isPMaxAspectRatiosV2Enabled]);

  const displayUrl = useMemo(() => getFinalUrlWithPath(finalUrls, urlPath1, urlPath2), [finalUrls, urlPath1, urlPath2]);

  const adPreview = useMemo(() => {
    const previewProps = {
      i18n,
      imageList: images && images.ImageList,
      updateImageList: onUpdateImageList,
      shortHeadline: headlines && headlines.Headline0,
      longHeadline: longHeadlines && longHeadlines.LongHeadline0,
      description: descriptions && descriptions.Description0,
      businessName,
      callToAction: assetGroup.data.CallToAction,
      displayUrl,
      url: first(finalUrls),
      previewImagesSharedState,
      setPreviewImagesSharedState,
    };
    return (
      <PerfMaxAdPreviewUAPL
        {...previewProps}
        logoPreviewEnabled={UAPLPMaxLogoPreviewEnabled}
        extensionsAssociations={extensionsAssociations}
        extensionFormatters={getExtensionFormatters(i18n)}
        isAdExtensionPreviewEnabled={PmaxExtensionPreview}
        isPMaxAspectRatiosV2Enabled={isPMaxAspectRatiosV2Enabled}
      />
    );
  }, [i18n, images, onUpdateImageList, headlines, longHeadlines, descriptions, businessName, assetGroup.data.CallToAction, displayUrl, finalUrls, previewImagesSharedState, UAPLPMaxLogoPreviewEnabled, extensionsAssociations, PmaxExtensionPreview, isPMaxAspectRatiosV2Enabled]);

  return (
    <div className={classes.assetGroupSettingsContentContainer}>
      <ActuationGroup actuationProps={{ isActive: areBeaconsActive }}>
        <AttentionBeaconGroup>
          <FormErrorGroup formErrorProps={{ errors: serverErrors, showAllValidationErrors }}>
            <ValidationGroup
              validationProps={{
                schema: AssetGroup,
                onValidate: onAssetGroupValidation,
                data: assetGroup.data,
                i18n: legacyI18n,
              }}
            >
              <CopilotRepositoryProvider
                campaignId={campaignId}
                finalUrl={finalUrlForCopilotRecommendations}
                locale={i18n.getLocale()}
                perfMarker={wizardPerfMarker}
                recommendationType="PMax"
                serverContext={serverContext}
                enableLogoAndCTA={CopilotCTALogoGeneration}
              >
                <div className="asset-group-controls-container">
                  {!hideStepHeader &&
                    <>
                      <h1 className="page-title">{i18n.getString(_TL_('Asset group'))}</h1>
                      <div className="page-description">
                        <span>{i18n.getString(_TL_('Performance Max campaigns consist of asset groups, which contain the information and media you want to include in your ads. We\'ll mix and match the most optimal asset combinations to create effective, high-performing ads.'))}</span>
                      </div>
                    </>
                  }
                  <UnmappedFormErrors className="other-server-errors" />
                  <div className="system-errors">
                    <ErrorMessages messages={systemErrors} />
                  </div>
                  {assetGroupNameSection}
                  {providerId && listingGroupsSection}
                </div>
                <div className="asset-group-controls-with-preview-container">
                  <div className="controls-affecting-preview-container">
                    {assetsSection}
                    {enhanceYourAdsSection}
                    {moreAssetsSection}
                    {moreOptionsSection}
                  </div>
                  <div className="asset-group-preview-container">
                    {adPreview}
                  </div>
                </div>
                <div className="asset-group-controls-container">
                  {audienceSignalSection}
                  {permissions.dynamic.SearchThemes && searchThemesSection}
                  {moreSettingsSection}
                  {imageExperienceComponents}
                </div>
              </CopilotRepositoryProvider>
            </ValidationGroup>
            {navigationConfirmationPrompt}
          </FormErrorGroup>
        </AttentionBeaconGroup>
      </ActuationGroup>

    </div>
  );
};

AssetGroupStep.propTypes = {
  stepService: PropTypes.shape({}).isRequired,
  scope: PropTypes.shape({
    values: PropTypes.shape({
      cid: PropTypes.number.isRequired,
      aid: PropTypes.number.isRequired,
      uid: PropTypes.number.isRequired,
      campaignId: PropTypes.number,
      assetGroupId: PropTypes.number,
    }).isRequired,
  }).isRequired,
  serverContext: PropTypes.shape({
    CurrentAccount: PropTypes.shape({
      Status: PropTypes.number,
    }).isRequired,
  }).isRequired,
  instrumentation: PropTypes.shape({
    AdsCopilot: PropTypes.shape({
      create: PropTypes.func,
    }),
  }).isRequired,
  wizardPerfMarker: PropTypes.shape({}).isRequired,
  permissions: PropTypes.shape({
    dynamic: PropTypes.shape({
      PerformanceMaxCampaignsFinalURLValidation: PropTypes.bool,
      PMaxAndDSAFinalURLValidation: PropTypes.bool,
    }).isRequired,
  }).isRequired,
};

export const StepAssetGroupShell = withStepLoadingShell(withInstrumentation(withServerContext(withScope(AssetGroupStep))));
